import { pruneSearch } from "@/lib/router-search";
import {
	buildEntityUrl,
	type EntityPathSegment,
	reencodeLiteralEntityTypePath,
} from "@/lib/url";
import { EVENT_FILTER_KEYS } from "./event-filters";

/**
 * The parsed shape of an entity splat (`/events/$`): the entity type slug
 * (first segment) and, for entity detail, the full path. An empty splat parses
 * to `{ typeSegment: undefined }`.
 */
type MemorySplat = {
	/** Raw (still URL-encoded) first path segment — the entity type. */
	typeSegment?: string;
	/** Full entity path — `type` or `type/slug/...`. */
	path?: string;
	/** True when the splat addresses a single entity (has a slug after the type). */
	isEntityDetail: boolean;
};

export function parseMemorySplat(splat: string | undefined): MemorySplat {
	const trimmed = (splat ?? "").replace(/^\/+|\/+$/g, "");
	if (!trimmed) return { isEntityDetail: false };
	const segments = trimmed.split("/");
	return {
		typeSegment: segments[0],
		path: trimmed,
		isEntityDetail: segments.length > 1,
	};
}

/** Link shape for the first-class type path (`/{owner}/<type>`). */
type MemoryTypeLink = {
	to: "/$owner/$entityType";
	params: { owner: string; entityType: string };
	search: { create: boolean | undefined };
};

/**
 * Additive scope for the Activity page: apply a filter patch while preserving
 * sibling event filters already in the URL. This is what makes the Activity
 * toolbar dropdowns COMBINABLE (type + connection_ids + feed_ids + agent AND
 * together) rather than mutually exclusive like a path scope, which replaces
 * the whole scope. A patch value of `undefined` clears that key.
 */
export function memoryEventsFilterSearch(
	ownerSlug: string,
	currentSearch: Record<string, unknown>,
	patch: Record<string, string | undefined>,
	entityPath?: string,
) {
	return {
		...(entityPath
			? {
					// This is already a path: wildcard params would encode its % a second time.
					to: reencodeLiteralEntityTypePath(
						`/${ownerSlug}/events/${entityPath}`,
					) as "/",
				}
			: { to: "/$owner/events" as const, params: { owner: ownerSlug } }),
		search: pruneSearch({
			...currentSearch,
			...patch,
			at: undefined,
			page: undefined,
		}) as Record<string, string | undefined>,
	};
}

/** Clear the complete Activity filter selection, including its record path. */
export function clearActivityFilterSearch(
	ownerSlug: string,
	currentSearch: Record<string, unknown>,
) {
	const keys = [
		...EVENT_FILTER_KEYS,
		"type",
		"entity",
		...Object.keys(currentSearch).filter((key) => key.startsWith("clf_")),
	];
	return memoryEventsFilterSearch(
		ownerSlug,
		currentSearch,
		Object.fromEntries(keys.map((key) => [key, undefined])),
	);
}

/** Scope to an entity type (path form: `/{owner}/<type>`). */
export function memoryBrowseTypeSearch(
	ownerSlug: string,
	entityTypeSlug: string,
): MemoryTypeLink {
	return {
		to: "/$owner/$entityType" as const,
		params: { owner: ownerSlug, entityType: entityTypeSlug },
		search: { create: undefined },
	};
}

/**
 * A single entity's page (`/{owner}/<type>/<slug>[/…]`), the same first-class
 * path form a type scope uses. Returns an href rather than a router link
 * object: the entity route is the `$owner/$` catch-all, so the whole path is
 * one splat, and `buildEntityUrl` already owns the `$`-type encoding.
 */
export function memoryBrowseEntitySearch(
	ownerSlug: string,
	segments: EntityPathSegment[],
): string {
	return buildEntityUrl(ownerSlug, segments);
}

/** The normalized `?connection_ids=` scope, accepting router-coerced numbers. */
export function parseConnectionScope(
	search: Record<string, unknown>,
): string | undefined {
	const raw = search.connection_ids;
	if (typeof raw === "number") return String(raw);
	return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}

function pickAgentScope(search: Record<string, unknown>): string | undefined {
	const raw = search.agent ?? search.agent_id;
	return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}

/** The `?agent=<agentId>` scope, if any. */
export function parseAgentScope(
	search: Record<string, unknown>,
): string | undefined {
	return pickAgentScope(search);
}

/** Drill from an agent Automation into its Activity log. */
export function memoryAutomationDrillSearch(
	ownerSlug: string,
	agentId: string,
	automationId: string,
) {
	const search: Record<string, string | undefined> = {
		agent: agentId,
		automation: automationId,
		schedule: undefined,
		platforms: undefined,
		connection_ids: undefined,
		run_ids: undefined,
		feed_ids: undefined,
		at: undefined,
	};

	return {
		to: "/$owner/events" as const,
		params: { owner: ownerSlug },
		search: pruneSearch(search) as Record<string, string | undefined>,
	};
}

/** Drill from a connector or entity feed row into its Activity log. */
export function memoryFeedDrillSearch(
	ownerSlug: string,
	feedId: number,
	options?: { connectionScope?: string },
) {
	return {
		to: "/$owner/events" as const,
		params: { owner: ownerSlug },
		search: pruneSearch({
			feed_ids: String(feedId),
			connection_ids: options?.connectionScope,
			agent: undefined,
			automation: undefined,
			schedule: undefined,
			platforms: undefined,
			run_ids: undefined,
			at: undefined,
		}) as Record<string, string | undefined>,
	};
}

/** Drill from a connection scope into Activity for that connector's ingestion. */
export function memoryConnectionDrillSearch(
	ownerSlug: string,
	connectionId: number | string,
) {
	return {
		to: "/$owner/events" as const,
		params: { owner: ownerSlug },
		search: pruneSearch({
			connection_ids: String(connectionId),
			feed_ids: undefined,
			agent: undefined,
			automation: undefined,
			at: undefined,
		}) as Record<string, string | undefined>,
	};
}

/**
 * Reverse-lookup: given the connector groups and a set of selected connection
 * ids, return the `connector_key` that owns all of them (the connector to expand
 * in the cascade dropdown / to build a "Manage" link for). Returns undefined
 * when any id is unmatched or belongs to another connector. Structurally typed
 * so this lib stays free of the connections-API import.
 */
export function connectorForConnectionIds(
	groups: ReadonlyArray<{
		connector_key: string;
		connections: ReadonlyArray<{ id: number }>;
	}>,
	connectionIds: ReadonlySet<string>,
): string | undefined {
	if (connectionIds.size === 0) return undefined;
	let match: string | undefined;
	const matchedIds = new Set<string>();
	for (const group of groups) {
		for (const connection of group.connections) {
			const id = String(connection.id);
			if (connectionIds.has(id)) {
				if (match && match !== group.connector_key) return undefined;
				match = group.connector_key;
				matchedIds.add(id);
			}
		}
	}
	return matchedIds.size === connectionIds.size ? match : undefined;
}

/** In-place scope for the built-in Members list (`?type=$member`). */
export function memoryMemberListSearch(ownerSlug: string) {
	return memoryBrowseTypeSearch(ownerSlug, "$member");
}

/** Open entity-type creation under the Memory destination. */
export function memoryCreateEntityTypeSearch(ownerSlug: string) {
	return {
		to: "/$owner/data" as const,
		params: { owner: ownerSlug },
		search: pruneSearch({
			create_type: "true",
		}) as Record<string, string | undefined>,
	};
}
